import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/features/auth/authStore';
import {
  listCategories,
  listProducts,
  getProduct,
  getCart,
  addToCart,
  removeFromCart,
  listPickupLocations,
  listMyOrders,
  getOrder,
  type AddToCartRequest,
  type Cart,
  type ShopCategory,
  type ProductDetail,
  type ProductQuery,
} from '@/api/shop';
import { SHOP_LEAGUE, toShopProducts, type ShopProduct } from './catalog';

// The cart lives on the SERVER now.
//
// It used to be a zustand store in the app: prices came from the hardcoded
// catalog, quantities never left the device, and checkout posted the whole
// basket for the server to re-price. That meant a cart could not survive a
// reinstall or follow a fan to another device, and stock was only ever checked
// at the last moment. These hooks read and mutate /shop/cart directly; every
// mutation returns the fresh cart, which is written straight into the query
// cache so the badge and the totals never disagree with the server.

const token = () => useAuthStore.getState().accessToken;
const useToken = () => useAuthStore((s) => s.accessToken);

export const shopKeys = {
  categories: (league: string) => ['shop', 'categories', league] as const,
  products: (league: string, params?: unknown) => ['shop', 'products', league, params ?? {}] as const,
  product: (id?: string) => ['shop', 'product', id] as const,
  pickupLocations: (league: string) => ['shop', 'pickup-locations', league] as const,
  cart: ['shop', 'cart'] as const,
  orders: ['shop', 'orders'] as const,
  order: (id?: string) => ['shop', 'order', id] as const,
};

export function useShopCategories(league: string = SHOP_LEAGUE) {
  return useQuery<ShopCategory[]>({
    queryKey: shopKeys.categories(league),
    queryFn: () => listCategories(league),
  });
}

/**
 * One page of the store, adapted for display.
 *
 * Category, search and paging are the server's job now: this used to fetch the
 * whole catalog — 733 products on the dev database — for a grid with no pager
 * and a search that filtered the downloaded array.
 *
 * `placeholderData` holds the current page on screen while the next one loads,
 * so paging and typing in the search box do not blank the grid.
 */
export function useShopProducts(
  params: Omit<ProductQuery, 'league'> & { league?: string; enabled?: boolean } = {},
) {
  const { league = SHOP_LEAGUE, enabled = true, ...rest } = params;
  const query = useQuery({
    queryKey: shopKeys.products(league, rest),
    queryFn: () => listProducts({ league, ...rest }),
    placeholderData: (previous) => previous,
    enabled,
  });
  const products = useMemo<ShopProduct[]>(
    () => toShopProducts(query.data?.products ?? []),
    [query.data],
  );
  return { ...query, products, total: query.data?.total ?? 0, priceRange: query.data?.price_range ?? null };
}

export function useProduct(id?: string) {
  return useQuery<ProductDetail>({
    queryKey: shopKeys.product(id),
    queryFn: () => getProduct(id as string),
    enabled: !!id,
  });
}

export function usePickupLocations(league: string = SHOP_LEAGUE) {
  return useQuery({
    queryKey: shopKeys.pickupLocations(league),
    queryFn: () => listPickupLocations(league),
  });
}

export function useCart() {
  const accessToken = useToken();
  const qc = useQueryClient();

  const query = useQuery<Cart>({
    queryKey: shopKeys.cart,
    queryFn: () => getCart(token() as string),
    enabled: !!accessToken,
  });

  const add = useMutation({
    mutationFn: (req: AddToCartRequest) => addToCart(token() as string, req),
    onSuccess: (cart) => qc.setQueryData(shopKeys.cart, cart),
  });

  const remove = useMutation({
    mutationFn: (itemId: string) => removeFromCart(token() as string, itemId),
    onSuccess: (cart) => qc.setQueryData(shopKeys.cart, cart),
  });

  const items = query.data?.items ?? [];
  return {
    ...query,
    cart: query.data,
    items,
    count: items.reduce((n, i) => n + i.quantity, 0),
    addItem: add.mutateAsync,
    removeItem: remove.mutateAsync,
    adding: add.isPending,
    removing: remove.isPending,
    mutationError: add.error ?? remove.error ?? null,
  };
}

/** Just the badge number — shares the cart query rather than fetching again. */
export function useCartCount(): number {
  const accessToken = useToken();
  const { data } = useQuery<Cart>({
    queryKey: shopKeys.cart,
    queryFn: () => getCart(token() as string),
    enabled: !!accessToken,
  });
  return (data?.items ?? []).reduce((n, i) => n + i.quantity, 0);
}

export function useMyOrders() {
  const accessToken = useToken();
  return useQuery({
    queryKey: shopKeys.orders,
    queryFn: () => listMyOrders(token() as string),
    enabled: !!accessToken,
  });
}

/** One order, polled while it is still moving. Terminal states stop the poll —
 *  a delivered order is not going to change on its own. */
export function useOrder(id?: string) {
  const accessToken = useToken();
  return useQuery({
    queryKey: shopKeys.order(id),
    queryFn: () => getOrder(token() as string, id as string),
    enabled: !!accessToken && !!id,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      const settled =
        status === 'delivered' ||
        status === 'picked_up' ||
        status === 'cancelled' ||
        status === 'refunded' ||
        status === 'returned' ||
        status === 'failed';
      // Waiting on a payment is the one state the fan is actively watching —
      // they just paid in another tab — so it is checked far more often.
      if (status === 'pending') return 4000;
      return settled ? false : 15000;
    },
  });
}
